import { logger } from '../../utils/logger';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  Compare as CompareIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FilingPeriodFilter from '../../components/common/FilingPeriodFilter';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiCardContent-root': {
    padding: theme.spacing(3),
  },
}));

const MetricCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  '& .metric-value': {
    fontSize: '2rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    marginBottom: theme.spacing(1),
  },
  '& .metric-label': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transaction-insights-tabpanel-${index}`}
      aria-labelledby={`transaction-insights-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TransactionInsights: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [batchResults, setBatchResults] = useState<any>(null);
  const [discrepancies, setDiscrepancies] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  
  // Variance insights data from backend
  const [varianceInsights, setVarianceInsights] = useState<any>(null);
  const [varianceTransactions, setVarianceTransactions] = useState<any[]>([]);

  const computedDerivedVarianceCount = useMemo(
    () =>
      varianceTransactions.filter((transaction) =>
        Math.abs(transaction.variance_amount || 0) > 0
      ).length,
    [varianceTransactions]
  );

  const computedVarianceAmount = useMemo(
    () =>
      varianceTransactions.reduce(
        (sum, transaction) => sum + (transaction.variance_amount || 0),
        0
      ),
    [varianceTransactions]
  );

  const computedExpectedTax = useMemo(
    () =>
      varianceTransactions.reduce(
        (sum, transaction) => sum + (transaction.expected_tax || 0),
        0
      ),
    [varianceTransactions]
  );

  const computedActualTax = useMemo(
    () =>
      varianceTransactions.reduce(
        (sum, transaction) => sum + (transaction.actual_tax || 0),
        0
      ),
    [varianceTransactions]
  );

  const transactionsWithVarianceDisplay = varianceTransactions.length;

  const netVarianceDisplay =
    varianceInsights?.transaction_insights?.net_variance_amount ??
    computedVarianceAmount;

  const totalVarianceMagnitude = varianceTransactions.reduce(
    (sum, transaction) => sum + Math.abs(transaction.variance_amount || 0),
    0
  );

  const safeExpectedTax = Number(computedExpectedTax || 0);
  const safeActualTax = Number(computedActualTax || 0);
  const safeNetVariance = Number(netVarianceDisplay || 0);
  
  // Date range filters
  const [filingPeriod, setFilingPeriod] = useState<string>('');
  const [lookbackPeriod, setLookbackPeriod] = useState<number>(12);
  const [availablePeriods, setAvailablePeriods] = useState<any[]>([]);
  
  // State filter from URL params
  const stateFilter = searchParams.get('state');
  
  // Single transaction analysis form
  const [transactionId, setTransactionId] = useState('');
  
  // Test calculation form
  const [testAddress, setTestAddress] = useState({
    address1: '123 Main St',
    city: 'Austin',
    state: 'TX',
    zip: '78701',
    country: 'US'
  });
  const [testAmount, setTestAmount] = useState(100);
  
  // Shopify test transactions
  const [shopifyTransactions, setShopifyTransactions] = useState<any[]>([]);
  const [selectedTransactions, setSelectedTransactions] = useState<number[]>([]);
  
  // Filters for discrepancies
  const [discrepancyFilters, setDiscrepancyFilters] = useState({
    varianceType: '',
    minVarianceAmount: '',
    startDate: '',
    endDate: '',
    limit: 50
  });

  // Memoized company ID
  const currentCompanyId = useMemo(() => {
    return companyId || user?.primary_company_id;
  }, [companyId, user]);

  // Fetch variance insights data
  const fetchVarianceInsights = useCallback(async () => {
    if (!currentCompanyId) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const params = new URLSearchParams();
      if (filingPeriod) {
        params.append('filing_period', filingPeriod);
      }
      if (lookbackPeriod > 1) params.append('lookback_period', lookbackPeriod.toString());

      const response = await fetch(`/api/v1/tax-variance/insights/${currentCompanyId}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.available_periods)) {
          setAvailablePeriods(data.available_periods);
        }
        if (data?.filing_period && data.filing_period !== filingPeriod) {
          setFilingPeriod(data.filing_period);
        }
        setVarianceInsights(data);
        logger.log('[TransactionInsights] Fetched variance insights:', data);
      } else {
        logger.error('[TransactionInsights] Failed to fetch variance insights:', response.status);
      }
    } catch (error) {
      logger.error('[TransactionInsights] Error fetching variance insights:', error);
    } finally {
      setLoading(false);
    }
  }, [currentCompanyId, filingPeriod, lookbackPeriod]);

  // Fetch variance transactions
  const fetchVarianceTransactions = useCallback(async () => {
    if (!currentCompanyId || !filingPeriod) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const params = new URLSearchParams();
      if (filingPeriod) params.append('filing_period', filingPeriod);
      if (lookbackPeriod > 1) params.append('lookback_period', lookbackPeriod.toString());
      if (stateFilter) params.append('state', stateFilter);
      params.append('limit', '100');

      const response = await fetch(`/api/v1/tax-variance/insights/${currentCompanyId}/transactions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setVarianceTransactions(data.transactions || []);
        logger.log('[TransactionInsights] Fetched variance transactions:', data);
      } else {
        logger.error('[TransactionInsights] Failed to fetch variance transactions:', response.status);
      }
    } catch (error) {
      logger.error('[TransactionInsights] Error fetching variance transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [currentCompanyId, filingPeriod, lookbackPeriod, stateFilter]);

  useEffect(() => {
    // Auto-load variance data on mount or when dependencies change
    if (currentCompanyId) {
      fetchVarianceInsights();
      fetchVarianceTransactions();
    }
  }, [currentCompanyId, filingPeriod, lookbackPeriod, stateFilter]);

  useEffect(() => {
    // Load Shopify test transactions on component mount
    loadShopifyTransactions();
  }, []);

  const loadShopifyTransactions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/platforms/transactions?limit=20&status=TAX_CALCULATED', { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
      if (response.ok) {
        const data = await response.json();
        setShopifyTransactions(data.transactions || []);
      }
    } catch (error) {
      logger.error('Error loading Shopify transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeTransaction = async (transactionId: string) => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/transaction-insights/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          transaction_id: parseInt(transactionId),
          force_recalculation: true
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setAnalysisResult(result);
      } else {
        const error = await response.json();
        logger.error('Analysis failed:', error);
        setAnalysisResult({ error: error.detail || 'Analysis failed' });
      }
    } catch (error) {
      logger.error('Error analyzing transaction:', error);
      setAnalysisResult({ error: 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  const analyzeBatchTransactions = async () => {
    if (selectedTransactions.length === 0) {
      alert('Please select transactions to analyze');
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch('/api/v1/transaction-insights/analyze-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          transaction_ids: selectedTransactions
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setBatchResults(result);
      } else {
        const error = await response.json();
        logger.error('Batch analysis failed:', error);
        setBatchResults({ error: error.detail || 'Batch analysis failed' });
      }
    } catch (error) {
      logger.error('Error in batch analysis:', error);
      setBatchResults({ error: 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  const testCalculation = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/transaction-insights/test-calculation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          address: testAddress,
          amount: testAmount,
          line_items: [
            {
              name: 'Test Product',
              sku: 'TEST-001',
              price: testAmount,
              quantity: 1
            }
          ]
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setAnalysisResult(result);
      } else {
        const error = await response.json();
        logger.error('Test calculation failed:', error);
        setAnalysisResult({ error: error.detail || 'Test calculation failed' });
      }
    } catch (error) {
      logger.error('Error in test calculation:', error);
      setAnalysisResult({ error: 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  const loadDiscrepancies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(discrepancyFilters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
      
      const response = await fetch(`/api/v1/transaction-insights/discrepancies?${params}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
      if (response.ok) {
        const result = await response.json();
        setDiscrepancies(result.transactions_with_discrepancies || []);
      }
    } catch (error) {
      logger.error('Error loading discrepancies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      setLoading(true);
      // Assuming company_id = 1 for demo, you can make this dynamic
      const response = await fetch('/api/v1/transaction-insights/summary/1?period=month', { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
      if (response.ok) {
        const result = await response.json();
        setSummary(result);
      }
    } catch (error) {
      logger.error('Error loading summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVarianceChip = (varianceType: string) => {
    const config = {
      exact_match: { color: 'success' as const, icon: <CheckCircleIcon /> },
      penny_variance: { color: 'info' as const, icon: <CheckCircleIcon /> },
      minor_variance: { color: 'warning' as const, icon: <WarningIcon /> },
      significant_variance: { color: 'error' as const, icon: <ErrorIcon /> }
    };
    
    const { color, icon } = config[varianceType as keyof typeof config] || 
                           { color: 'default' as const, icon: <WarningIcon /> };
    
    return (
      <Chip
        label={varianceType.replace('_', ' ').toUpperCase()}
        color={color}
        size="small"
        icon={icon}
      />
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Load data when switching tabs
    switch (newValue) {
      case 0: // Variance Insights
        fetchVarianceInsights();
        break;
      case 1: // Variance Transactions
        fetchVarianceTransactions();
        break;
      case 4: // Discrepancies
        loadDiscrepancies();
        break;
      case 5: // Summary
        loadSummary();
        break;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <AnalyticsIcon color="primary" />
        Transaction Tax Insights
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Analyze tax calculations, detect discrepancies, and optimize tax accuracy using AI-powered insights
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="transaction insights tabs">
          <Tab label="Variance Insights" icon={<AnalyticsIcon />} />
          <Tab label="Variance Transactions" icon={<AssessmentIcon />} />
          <Tab label="Single Analysis" icon={<SearchIcon />} />
          <Tab label="Batch Analysis" icon={<AssessmentIcon />} />
          <Tab label="Discrepancies" icon={<WarningIcon />} />
          <Tab label="Summary" icon={<AnalyticsIcon />} />
        </Tabs>
      </Box>

      {/* Variance Insights */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tax Variance Analysis
                </Typography>
                
                {/* Date Range Filters */}
                <Box sx={{ mb: 3 }}>
                  <FilingPeriodFilter
                    filingPeriod={filingPeriod}
                    onFilingPeriodChange={setFilingPeriod}
                    lookbackPeriod={lookbackPeriod}
                    onLookbackPeriodChange={setLookbackPeriod}
                    availablePeriods={availablePeriods}
                    onRefresh={() => {
                      fetchVarianceInsights();
                      fetchVarianceTransactions();
                    }}
                    loading={loading}
                    showLookbackPeriod={true}
                    showRefreshButton={true}
                    size="small"
                    fullWidth={false}
                  />
                </Box>

                {/* State Filter Display */}
                {stateFilter && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Filtering transactions for state: <strong>{stateFilter}</strong>
                  </Alert>
                )}

                {/* Variance Insights Data */}
                {varianceInsights ? (
                  <Grid container spacing={3}>
                    {/* Actual vs Expected Tax */}
                    <Grid item xs={12} md={6}>
                      <MetricCard>
                        <div className="metric-value">
                          ${varianceInsights.actual_sales_tax?.total_collected?.toFixed(2) || '0.00'}
                        </div>
                        <div className="metric-label">Actual Tax Collected</div>
                      </MetricCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MetricCard>
                        <div className="metric-value">
                          ${varianceInsights.expected_sales_tax?.total_due?.toFixed(2) || '0.00'}
                        </div>
                        <div className="metric-label">Expected Tax Due</div>
                      </MetricCard>
                    </Grid>

                    {/* Transaction Insights */}
                    <Grid item xs={12}>
                      <StyledCard>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Transaction Insights
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="text.secondary">
                                New Transactions
                              </Typography>
                              <Typography variant="h6">
                                {varianceInsights.transaction_insights?.new_transactions || 0}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="text.secondary">
                                With Variance
                              </Typography>
                              <Typography variant="h6">
                                {transactionsWithVarianceDisplay}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="text.secondary">
                                Net Variance
                              </Typography>
                              <Typography variant="h6" color={safeNetVariance >= 0 ? 'success.main' : 'error.main'}>
                                ${safeNetVariance.toFixed(2)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="text.secondary">
                                Total Variance
                              </Typography>
                              <Typography variant="h6" color="text.secondary">
                                ${totalVarianceMagnitude.toFixed(2)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="text.secondary">
                                Filing Period
                              </Typography>
                              <Typography variant="h6">
                                {varianceInsights.transaction_insights?.filing_period || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="text.secondary">
                                GoTruTax Expected Tax
                              </Typography>
                              <Typography variant="h6">
                                ${safeExpectedTax.toFixed(2)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="text.secondary">
                                Source Actual Tax
                              </Typography>
                              <Typography variant="h6">
                                ${safeActualTax.toFixed(2)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </StyledCard>
                    </Grid>

                    {/* Variance Breakdown */}
                    {varianceInsights.transaction_insights?.variance_breakdown && (
                      <Grid item xs={12}>
                        <StyledCard>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Variance Breakdown
                            </Typography>
                            <Grid container spacing={2}>
                              {Object.entries(varianceInsights.transaction_insights.variance_breakdown).map(([key, value]) => (
                                <Grid item xs={6} sm={3} key={key}>
                                  <Typography variant="body2" color="text.secondary">
                                    {key.replace('_', ' ').toUpperCase()}
                                  </Typography>
                                  <Typography variant="h6">
                                    {value as number}
                                  </Typography>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </StyledCard>
                      </Grid>
                    )}
                  </Grid>
                ) : loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Alert severity="info">
                    No variance insights data available. Make sure you have transactions with tax calculations.
                  </Alert>
                )}
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Variance Transactions */}
      <TabPanel value={tabValue} index={1}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Variance Transactions
            </Typography>
            
            {varianceTransactions.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Transaction ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Actual Tax</TableCell>
                      <TableCell>Expected Tax</TableCell>
                      <TableCell>Variance</TableCell>
                      <TableCell>Variance %</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {varianceTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{transaction.transaction_id}</TableCell>
                        <TableCell>{new Date(transaction.transaction_date).toLocaleDateString()}</TableCell>
                        <TableCell>${transaction.transaction_amount?.toFixed(2)}</TableCell>
                        <TableCell>${transaction.actual_tax?.toFixed(2)}</TableCell>
                        <TableCell>${transaction.expected_tax?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Typography color={transaction.variance_amount >= 0 ? 'success.main' : 'error.main'}>
                            ${transaction.variance_amount?.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>{transaction.variance_percentage?.toFixed(2)}%</TableCell>
                        <TableCell>{getVarianceChip(transaction.variance_type)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Alert severity="info">
                No variance transactions found for the selected period.
              </Alert>
            )}
          </CardContent>
        </StyledCard>
      </TabPanel>

      {/* Single Transaction Analysis */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analyze Shopify Transaction
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Transaction ID"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="Enter transaction ID from Shopify"
                    margin="normal"
                  />
                  <Button
                    variant="contained"
                    onClick={() => analyzeTransaction(transactionId)}
                    disabled={!transactionId || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                    sx={{ mt: 2 }}
                  >
                    Analyze Transaction
                  </Button>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="h6" gutterBottom>
                  Test Tax Calculation
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={testAddress.address1}
                      onChange={(e) => setTestAddress({...testAddress, address1: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={testAddress.city}
                      onChange={(e) => setTestAddress({...testAddress, city: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="State"
                      value={testAddress.state}
                      onChange={(e) => setTestAddress({...testAddress, state: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={testAddress.zip}
                      onChange={(e) => setTestAddress({...testAddress, zip: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      value={testAmount}
                      onChange={(e) => setTestAmount(parseFloat(e.target.value))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={testCalculation}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <CompareIcon />}
                    >
                      Test Calculation
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} md={6}>
            {analysisResult && (
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Analysis Results
                  </Typography>
                  
                  {analysisResult.error ? (
                    <Alert severity="error">{analysisResult.error}</Alert>
                  ) : (
                    <Box>
                      {/* Calculation Method */}
                      {analysisResult.calculated_tax && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">Calculation Method:</Typography>
                          <Chip 
                            label={analysisResult.calculated_tax.calculation_method || 'Unknown'}
                            color="primary"
                            sx={{ mr: 1 }}
                          />
                          <Chip 
                            label={`Confidence: ${analysisResult.calculated_tax.confidence || 'Medium'}`}
                            color="info"
                          />
                        </Box>
                      )}
                      
                      {/* Tax Amounts */}
                      {analysisResult.calculated_tax && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">Tax Calculation:</Typography>
                          <Typography>
                            Tax Amount: ${analysisResult.calculated_tax.tax_amount?.toFixed(2) || '0.00'}
                          </Typography>
                          <Typography>
                            State: {analysisResult.calculated_tax.state || 'Unknown'}
                          </Typography>
                          <Typography>
                            Source: {analysisResult.calculated_tax.source || 'Unknown'}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Discrepancy Analysis */}
                      {analysisResult.discrepancy_analysis && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">Discrepancy Analysis:</Typography>
                          {getVarianceChip(analysisResult.discrepancy_analysis.variance_type)}
                          <Typography sx={{ mt: 1 }}>
                            Variance: ${analysisResult.discrepancy_analysis.calculated_variance?.toFixed(2) || '0.00'}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Recommendations */}
                      {analysisResult.recommendations && analysisResult.recommendations.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2">Recommendations:</Typography>
                          {analysisResult.recommendations.map((rec: string, index: number) => (
                            <Typography key={index} variant="body2" sx={{ ml: 1 }}>
                              • {rec}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      
                      {/* Test Calculation Results */}
                      {analysisResult.test_calculation && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2">Test Results:</Typography>
                          <pre style={{ fontSize: '0.875rem', background: 'var(--color-paper-3)', padding: '8px', borderRadius: '4px' }}>
                            {JSON.stringify(analysisResult.test_calculation, null, 2)}
                          </pre>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </StyledCard>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* Batch Analysis */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Shopify Test Transactions
                  </Typography>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadShopifyTransactions}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Box>
                
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTransactions(shopifyTransactions.map(t => t.id));
                              } else {
                                setSelectedTransactions([]);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>ID</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Tax</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shopifyTransactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell padding="checkbox">
                            <input
                              type="checkbox"
                              checked={selectedTransactions.includes(transaction.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTransactions([...selectedTransactions, transaction.id]);
                                } else {
                                  setSelectedTransactions(selectedTransactions.filter(id => id !== transaction.id));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>{transaction.id}</TableCell>
                          <TableCell>{new Date(transaction.transaction_date).toLocaleDateString()}</TableCell>
                          <TableCell>${transaction.amount?.toFixed(2)}</TableCell>
                          <TableCell>${transaction.tax_amount?.toFixed(2)}</TableCell>
                          <TableCell>{transaction.customer_name}</TableCell>
                          <TableCell>
                            <Chip 
                              label={transaction.status} 
                              size="small"
                              color={transaction.status === 'TAX_CALCULATED' ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={analyzeBatchTransactions}
                    disabled={selectedTransactions.length === 0 || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
                  >
                    Analyze Selected ({selectedTransactions.length})
                  </Button>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            {batchResults && (
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Batch Analysis Results
                  </Typography>
                  
                  {batchResults.error ? (
                    <Alert severity="error">{batchResults.error}</Alert>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2">Summary:</Typography>
                      <Typography>Total: {batchResults.summary?.total_transactions || 0}</Typography>
                      <Typography>Success: {batchResults.summary?.successful_analyses || 0}</Typography>
                      <Typography>Failed: {batchResults.summary?.failed_analyses || 0}</Typography>
                      <Typography>Variances: {batchResults.summary?.significant_variances || 0}</Typography>
                      <Typography>
                        Total Variance: ${batchResults.summary?.total_variance_amount?.toFixed(2) || '0.00'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </StyledCard>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* Discrepancies */}
      <TabPanel value={tabValue} index={4}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tax Discrepancy Detection
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Variance Type</InputLabel>
                  <Select
                    value={discrepancyFilters.varianceType}
                    onChange={(e: SelectChangeEvent) => setDiscrepancyFilters({...discrepancyFilters, varianceType: e.target.value})}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="significant_variance">Significant</MenuItem>
                    <MenuItem value="minor_variance">Minor</MenuItem>
                    <MenuItem value="penny_variance">Penny</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Min Variance ($)"
                  type="number"
                  value={discrepancyFilters.minVarianceAmount}
                  onChange={(e) => setDiscrepancyFilters({...discrepancyFilters, minVarianceAmount: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={discrepancyFilters.startDate}
                  onChange={(e) => setDiscrepancyFilters({...discrepancyFilters, startDate: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={discrepancyFilters.endDate}
                  onChange={(e) => setDiscrepancyFilters({...discrepancyFilters, endDate: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={loadDiscrepancies}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ height: '56px' }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Actual Tax</TableCell>
                    <TableCell>Calculated Tax</TableCell>
                    <TableCell>Variance</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Method</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {discrepancies.map((discrepancy) => (
                    <TableRow key={discrepancy.transaction_id}>
                      <TableCell>{discrepancy.transaction_id}</TableCell>
                      <TableCell>{new Date(discrepancy.transaction_date).toLocaleDateString()}</TableCell>
                      <TableCell>${discrepancy.amount?.toFixed(2)}</TableCell>
                      <TableCell>${discrepancy.actual_tax?.toFixed(2)}</TableCell>
                      <TableCell>${discrepancy.calculated_tax?.toFixed(2)}</TableCell>
                      <TableCell>${discrepancy.variance_amount?.toFixed(2)}</TableCell>
                      <TableCell>{getVarianceChip(discrepancy.variance_type)}</TableCell>
                      <TableCell>
                        <Chip label={discrepancy.calculation_method} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>
      </TabPanel>

      {/* Summary */}
      <TabPanel value={tabValue} index={5}>
        {summary && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard>
                <div className="metric-value">{summary.summary?.total_transactions || 0}</div>
                <div className="metric-label">Total Transactions</div>
              </MetricCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard>
                <div className="metric-value">{summary.summary?.accuracy_rate || 0}%</div>
                <div className="metric-label">Accuracy Rate</div>
              </MetricCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard>
                <div className="metric-value">${summary.summary?.total_variance_amount?.toFixed(2) || '0.00'}</div>
                <div className="metric-label">Total Variance</div>
              </MetricCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard>
                <div className="metric-value">${summary.summary?.average_variance?.toFixed(4) || '0.0000'}</div>
                <div className="metric-label">Avg Variance</div>
              </MetricCard>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Variance Breakdown
                  </Typography>
                  {summary.variance_breakdown && Object.entries(summary.variance_breakdown).map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{key.replace('_', ' ').toUpperCase()}:</Typography>
                      <Typography fontWeight="bold">{value as number}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </StyledCard>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Calculation Methods
                  </Typography>
                  {summary.calculation_methods && Object.entries(summary.calculation_methods).map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{key}:</Typography>
                      <Typography fontWeight="bold">{value as number}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </StyledCard>
            </Grid>
            
            {summary.recommendations && summary.recommendations.length > 0 && (
              <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recommendations
                    </Typography>
                    {summary.recommendations.map((rec: string, index: number) => (
                      <Alert key={index} severity="info" sx={{ mb: 1 }}>
                        {rec}
                      </Alert>
                    ))}
                  </CardContent>
                </StyledCard>
              </Grid>
            )}
          </Grid>
        )}
      </TabPanel>
    </Box>
  );
};

export default TransactionInsights; 
